import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { isAuthEnabled, tokenRequest } from "../authConfig";
import { getBackendUri } from "../api/backendUri";

export enum UserRole {
    ADMIN = "Admin",
    USER = "User",
    ANALYTICS = "Analytics"
}

export interface UserInfo {
    user_id: string;
    email: string | null;
    display_name: string | null;
    roles: UserRole[];
    is_admin: boolean;
    is_user: boolean;
    is_analytics: boolean;
}

interface AuthContextType {
    user: UserInfo | null;
    loading: boolean;
    error: string | null;
    isAuthenticated: boolean;
    getAccessToken: () => Promise<string | null>;
    hasRole: (role: UserRole) => boolean;
    isAdmin: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const BACKEND_CONNECTIVITY_ERROR =
    "Network error: the backend is not reachable. This app is accessible only through the internal network or VPN.";

export function AuthProvider({ children }: { readonly children: ReactNode }) {
    const { instance, accounts, inProgress } = useMsal();
    const msalAuthenticated = useIsAuthenticated();

    const [user, setUser] = useState<UserInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const getAccessToken = useCallback(async (): Promise<string | null> => {
        if (!isAuthEnabled || accounts.length === 0) return null;

        try {
            const response = await instance.acquireTokenSilent({
                ...tokenRequest,
                account: accounts[0]
            });
            return response.accessToken;
        } catch (err: any) {
            console.error("Failed to acquire token silently:", err);
            if (err.errorCode === "interaction_required" || err.errorCode === "consent_required") {
                try {
                    await instance.acquireTokenRedirect({
                        ...tokenRequest,
                        account: accounts[0]
                    });
                } catch (redirectError) {
                    console.error("Failed to acquire token via redirect:", redirectError);
                }
            }
            return null;
        }
    }, [instance, accounts]);

    useEffect(() => {
        async function fetchUserInfo() {
            if (!isAuthEnabled) {
                setUser({
                    user_id: "dev-user",
                    email: "dev@localhost",
                    display_name: "Development User",
                    roles: [UserRole.ADMIN],
                    is_admin: true,
                    is_user: true,
                    is_analytics: true
                });
                setLoading(false);
                return;
            }

            if (inProgress !== InteractionStatus.None) return;

            if (!msalAuthenticated) {
                setUser(null);
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                const token = await getAccessToken();
                if (!token) {
                    setUser(null);
                    setLoading(false);
                    return;
                }

                const backendUri = getBackendUri();
                const url = backendUri ? `${backendUri}/auth/me` : "/auth/me";
                const resp = await fetch(url, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (!resp.ok) {
                    throw new Error(`Auth API returned ${resp.status}`);
                }
                const data: UserInfo = await resp.json();
                setUser(data);
                setError(null);
            } catch (err: any) {
                console.error("Failed to fetch user info:", err);
                const message = String(err?.message || "");
                const isConnectivityError =
                    err instanceof TypeError ||
                    message.includes("Failed to fetch") ||
                    message.includes("NetworkError") ||
                    message.includes("Load failed");
                setError(isConnectivityError ? BACKEND_CONNECTIVITY_ERROR : "Failed to load user information");
                setUser(null);
            } finally {
                setLoading(false);
            }
        }

        fetchUserInfo();
    }, [msalAuthenticated, inProgress, getAccessToken]);

    const hasRole = useCallback(
        (role: UserRole): boolean => user?.roles.includes(role) ?? false,
        [user]
    );

    const isAdmin = useCallback((): boolean => user?.is_admin ?? false, [user]);

    const value: AuthContextType = useMemo(
        () => ({
            user,
            loading,
            error,
            isAuthenticated: !isAuthEnabled || msalAuthenticated,
            getAccessToken,
            hasRole,
            isAdmin
        }),
        [user, loading, error, msalAuthenticated, getAccessToken, hasRole, isAdmin]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
}
